import React, { useState, useEffect, useCallback } from "react";
import { Grid, Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import imageCompression from "browser-image-compression";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Swal from "sweetalert2";
import { ServerBaseUrlForStaticImages } from "constants";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";
import { validateFiles, validateProduct, validateSpecifications } from "../validations";
import { useNavigate } from "react-router-dom";
import LogoutButton from "layouts/logout";
import { getCroppedImg } from "../../../core/utils/cropImage";
import ProductTable from "./components/ProductTable";
import MediaModal from "./components/MediaModal";
import EditProductModal from "./components/EditProductModal";
import AddProductModal from "./components/AddProductModal";
import CropModal from "./components/CropModal";
import ProductDetailsModal from "./components/ProductsDetailModal";
import DeleteProductModal from "./components/DeleteProductModal";
import FullScreenMediaModal from "./components/FullScreenMediaModal";

function ProductDetails() {
  const [baseURL] = useState(ServerBaseUrlForStaticImages);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openContentModel, setOpenContentModel] = useState(false);
  const [openMediaModel, setOpenMediaModel] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [keywordsError, setKeywordsError] = useState("");
  const [editKeywordsError, setEditKeywordsError] = useState("");
  const [canSubmitEdit, setCanSubmitEdit] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [touched, setTouched] = useState({});
  const [editTouched, setEditTouched] = useState({});

  const [openCropModal, setOpenCropModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const CROP_ASPECT_RATIO = 800 / 950;

  const [croppingQueue, setCroppingQueue] = useState([]);
  const [currentCropIndex, setCurrentCropIndex] = useState(0);
  const [cropModalMultiOpen, setCropModalMultiOpen] = useState(false);

  const [Input, setInput] = useState({
    product_name: "",
    sku: "",
    price: "",
    stock_quantity: "",
    discount_price: "",
    discount: "",
    discounted_price: "",
    gst_type: "",
    gst_percentage: "",
    parent_category_name: "",
    specification: [{ title: "", desc: "" }],
    description: "",
    warranty_description: "",
    product_images: [],
    product_videos: [],
    keywords: [],
  });

  const [editInput, setEditInput] = useState({});
  const [deleteProductId, setDeleteProductId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const email = localStorage.getItem("email");
  const mobile_number = localStorage.getItem("mobile_number");
  const [selectedProduct, setSelectedProduct] = useState({});
  const [isDealerPriceEnabled, setIsDealerPriceEnabled] = useState(false);
  const [openFullScreen, setOpenFullScreen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isVideo, setIsVideo] = useState(false);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [specificationErrors, setSpecificationErrors] = useState([]);
  const [errors, setErrors] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [imageErrors, setImageErrors] = useState([]);
  const [videoErrors, setVideoErrors] = useState([]);

  useEffect(() => {
    disableBackNavigation();
    fetchProducts();
    fetchCategories();
    if (Input.specification.length === 0) {
      setInput((prev) => ({
        ...prev,
        specification: [{ title: "", desc: "" }],
      }));
    }
  }, []);

  useEffect(() => {
    const stockQty = Number(editInput.stock_quantity);
    setIsDealerPriceEnabled(stockQty > 25);
  }, [editInput.stock_quantity]);

  useEffect(() => {
    if (!editInput.product_id) {
      setCanSubmitEdit(false);
      return;
    }

    const hasRequiredFields =
      editInput.product_name?.trim() &&
      editInput.sku?.trim() &&
      editInput.price &&
      parseFloat(editInput.price) > 0 &&
      editInput.stock_quantity !== undefined &&
      editInput.stock_quantity !== null &&
      editInput.stock_quantity !== "" &&
      parseInt(editInput.stock_quantity) >= 0 &&
      editInput.gst_type?.trim() &&
      editInput.category_name?.trim() &&
      editInput.description?.trim();

    const hasValidGST =
      editInput.gst_type === "No GST" ||
      (editInput.gst_percentage && parseFloat(editInput.gst_percentage) >= 0);

    const stockQty = parseInt(editInput.stock_quantity);
    const hasValidDealerPrice =
      stockQty <= 25 ||
      (editInput.discount_price !== undefined &&
        editInput.discount_price !== null &&
        editInput.discount_price !== "" &&
        parseFloat(editInput.discount_price) >= 0);

    let keywordsArray = editInput.keywords;
    if (typeof keywordsArray === 'string') {
      keywordsArray = keywordsArray.split(",").map(k => k.trim()).filter(Boolean);
    }

    const hasValidKeywords =
      Array.isArray(keywordsArray) &&
      keywordsArray.length > 0 &&
      !editKeywordsError;

    const hasValidSpecifications =
      Array.isArray(editInput.specification) &&
      editInput.specification.length > 0 &&
      editInput.specification.every((spec) => {
        const hasTitle = spec.title?.trim();
        const hasDesc = spec.desc?.trim();
        return hasTitle && hasDesc;
      }) &&
      !specificationErrors.some((specError) =>
        Object.values(specError).some((e) => e)
      );

    const hasNoErrors = Object.values(errors).every((e) => !e);

    const valid =
      hasRequiredFields &&
      hasValidGST &&
      hasValidDealerPrice &&
      hasValidKeywords &&
      hasValidSpecifications &&
      hasNoErrors;

    setCanSubmitEdit(!!valid);
  }, [editInput, editInput.specification, editKeywordsError, specificationErrors, errors]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await api.get("/product/fetch", { params: { email } });
      const products = response.data.products;
      if (Array.isArray(products)) {
        const fetchedProducts = products.map((product) => ({
          ...product,
          product_images: Array.isArray(product.product_images)
            ? product.product_images.map(
                (img) =>
                  `${baseURL}/product/media/${img.replace(/^\/+/, "")}`
              )
            : [],
          product_videos: Array.isArray(product.product_videos)
            ? product.product_videos.map(
                (vid) =>
                  `${baseURL}/product/media/${vid.replace(/^\/+/, "")}`
              )
            : [],
        }));

        setProducts(fetchedProducts);
        if (page * rowsPerPage >= fetchedProducts.length && fetchedProducts.length > 0) {
          setPage(0);
        } else if (fetchedProducts.length === 0) {
          setPage(0);
        }
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      Swal.fire(
        "Error",
        "Failed to load products. Please try again later.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get("/category/fetch", { params: { email } });
      setCategories(res.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const validateAddForm = () => {
    const newErrors = {};
    const newTouched = {};

    if (!Input.product_name?.trim()) {
      newErrors.product_name = "Product name is required";
      newTouched.product_name = true;
    }

    if (!Input.price?.trim()) {
      newErrors.price = "Price is required";
      newTouched.price = true;
    } else if (parseFloat(Input.price) <= 0) {
      newErrors.price = "Price must be greater than 0";
      newTouched.price = true;
    }

    if (!Input.stock_quantity?.trim()) {
      newErrors.stock_quantity = "Stock quantity is required";
      newTouched.stock_quantity = true;
    } else if (parseInt(Input.stock_quantity) < 0) {
      newErrors.stock_quantity = "Stock quantity cannot be negative";
      newTouched.stock_quantity = true;
    }

    if (isDealerPriceEnabled && !Input.discount_price?.trim()) {
      newErrors.discount_price = "Dealer price is required when stock > 20";
      newTouched.discount_price = true;
    }

    if (!Input.gst_type?.trim()) {
      newErrors.gst_type = "GST type is required";
      newTouched.gst_type = true;
    }

    if (
      (Input.gst_type === "Include GST" || Input.gst_type === "Exclude GST") &&
      !Input.gst_percentage?.trim()
    ) {
      newErrors.gst_percentage = "GST percentage is required";
      newTouched.gst_percentage = true;
    }

    if (!Input.parent_category_name?.trim()) {
      newErrors.parent_category_name = "Category is required";
      newTouched.parent_category_name = true;
    }

    if (!Input.description?.trim()) {
      newErrors.description = "Description is required";
      newTouched.description = true;
    }

    if (!Input.keywords || Input.keywords.length === 0) {
      setKeywordsError("Please enter at least one keyword");
      newTouched.keywords = true;
    } else {
      setKeywordsError("");
    }

    if (images.length < 1) {
      setImageErrors(["At least 1 image is required"]);
      newTouched.images = true;
    } else if (images.length > 4) {
      setImageErrors([`Maximum 4 images allowed. You have ${images.length}`]);
      newTouched.images = true;
    } else {
      setImageErrors([]);
    }

    const specErrors = validateSpecifications(Input.specification || []);
    setSpecificationErrors(specErrors);

    setErrors(newErrors);
    setTouched({ ...touched, ...newTouched });

    const hasNoErrors =
      Object.keys(newErrors).length === 0 &&
      !keywordsError &&
      imageErrors.length === 0 &&
      images.length >= 1 &&
      images.length <= 4 &&
      !specErrors.some((err) => Object.values(err).some((e) => e));

    return hasNoErrors;
  };

  const handleAddChange = (e) => {
    const { name, value } = e.target;

    if (name === "keywords" && value.length > 0) {
      setKeywordsError("");
      setTouched((prev) => ({ ...prev, keywords: true }));
    }

    let updatedValue = value;
    let newInput = { ...Input, [name]: updatedValue };

    if (name === "gst_type") {
      if (value === "No GST") {
        setInput((prev) => ({
          ...prev,
          gst_type: updatedValue,
          gst_percentage: "",
        }));
        setErrors((prev) => ({ ...prev, gst_percentage: "" }));
        setTouched((prev) => ({ ...prev, gst_type: true }));
        return;
      }
    }

    if (newInput.price && newInput.discounted_price) {
      const priceNum = parseFloat(newInput.price);
      const discountedNum = parseFloat(newInput.discounted_price);
      if (priceNum > 0 && discountedNum >= 0) {
        const discountCalc = ((priceNum - discountedNum) / priceNum) * 100;
        newInput.discount = Number.isInteger(discountCalc)
          ? discountCalc.toString()
          : discountCalc.toFixed(2).replace(/\.00$/, "");
      } else {
        newInput.discount = "";
      }
    }

    setInput(newInput);

    if (touched[name]) {
      const validationErrors = validateProduct(newInput);
      setErrors({ ...errors, [name]: validationErrors[name] || "" });
    }

    setTouched((prev) => ({ ...prev, [name]: true }));

    if (name === "stock_quantity") {
      setIsDealerPriceEnabled(Number(value) > 20);
    }
  };

  const handlespecificationChange = (index, field, value) => {
    setInput((prevInput) => {
      const updatedspecification = [
        ...(prevInput.specification || [{ title: "", desc: "" }]),
      ];
      updatedspecification[index][field] = value;
      const errors = validateSpecifications(updatedspecification);
      setSpecificationErrors(errors);
      return { ...prevInput, specification: updatedspecification };
    });
  };

  const addspecificationRow = () => {
    setInput((prevInput) => ({
      ...prevInput,
      specification: [
        ...(prevInput.specification || []),
        { title: "", desc: "" },
      ],
    }));
  };

  const removespecificationRow = (index) => {
    setInput((prevInput) => ({
      ...prevInput,
      specification: (prevInput.specification || []).filter(
        (_, i) => i !== index
      ),
    }));
  };

  const handleImageSelectForCrop = async (event) => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    const currentCount = images.length;
    const totalAfterAdd = currentCount + imageFiles.length;

    event.target.value = "";

    if (imageFiles.length === 0) {
      setImageErrors(["Please select at least one image"]);
      setTouched((prev) => ({ ...prev, images: true }));
      return;
    }

    if (totalAfterAdd > 4) {
      setImageErrors([
        `Maximum 4 images allowed. You already have ${currentCount}. You can add up to ${4 - currentCount} more.`,
      ]);
      setSnackbar({
        open: true,
        severity: "error",
        message: `Maximum 4 images allowed. Current: ${currentCount}`,
      });
      setTouched((prev) => ({ ...prev, images: true }));
      return;
    }

    const processedFiles = [];
    const validationErrors = [];

    for (const file of imageFiles) {
      let fileToUse = file;

      if (file.size > 1024 * 1024 * 5) {
        try {
          const options = {
            maxSizeMB: 5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          };
          fileToUse = await imageCompression(file, options);
        } catch (error) {
          console.error("Image compression error:", error);
        }
      }

      const errors = validateFiles([fileToUse], "image");
      if (errors.length > 0) {
        validationErrors.push(`${file.name}: ${errors.join(", ")}`);
        console.log("Validation failed for:", file.name, errors);
        continue;
      }

      processedFiles.push(fileToUse);
    }

    if (validationErrors.length > 0) {
      setImageErrors(validationErrors);
      setSnackbar({
        open: true,
        severity: "error",
        message: `Some images failed validation: ${validationErrors[0]}`,
      });
    }

    if (processedFiles.length === 0) {
      console.log("No valid files to process");
      return;
    }

    const queue = processedFiles.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      cropped: false,
      croppedBlob: null,
      cropState: null,
    }));

    console.log("Opening crop modal with", queue.length, "images");
    setCroppingQueue(queue);
    setCurrentCropIndex(0);
    setCropModalMultiOpen(true);
    setImageErrors([]);
  };

  const handleCropNavigation = (newIndex) => {
    if (newIndex >= 0 && newIndex < croppingQueue.length) {
      setCurrentCropIndex(newIndex);
    }
  };

  const handleSaveCurrentCrop = async (
    index,
    cropPixels,
    cropState,
    skipFile = null
  ) => {
    const updatedQueue = [...croppingQueue];

    if (skipFile) {
      updatedQueue[index].cropped = true;
      updatedQueue[index].croppedBlob = skipFile;
      updatedQueue[index].cropState = null;
    } else if (cropPixels) {
      updatedQueue[index].cropState = cropState;
      try {
        const croppedBlob = await getCroppedImg(
          croppingQueue[index].url,
          cropPixels
        );
        updatedQueue[index].cropped = true;
        updatedQueue[index].croppedBlob = croppedBlob;
      } catch (error) {
        console.error("Error cropping image:", error);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to crop image",
        });
        return;
      }
    }

    setCroppingQueue(updatedQueue);
  };

  const handleFinishCropping = () => {
    const finalImages = croppingQueue
      .filter((img) => img.cropped && img.croppedBlob)
      .map((img) => img.croppedBlob);

    if (finalImages.length === 0) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "No images were cropped. Please crop at least one image.",
      });
      return;
    }

    const totalAfterAdd = images.length + finalImages.length;
    if (totalAfterAdd > 4) {
      setSnackbar({
        open: true,
        severity: "error",
        message: `Maximum 4 images allowed. After cropping you would have ${totalAfterAdd}.`,
      });
      return;
    }

    setImages((prev) => [...prev, ...finalImages]);

    croppingQueue.forEach((img) => URL.revokeObjectURL(img.url));
    setCropModalMultiOpen(false);
    setCroppingQueue([]);
    setCurrentCropIndex(0);

    setSnackbar({
      open: true,
      severity: "success",
      message: `Successfully added ${finalImages.length} image(s)! Total: ${images.length + finalImages.length}`,
    });

    setImageErrors([]);
    setTouched((prev) => ({ ...prev, images: true }));
  };

  const handleCropModalMultiClose = () => {
    Swal.fire({
      title: "Cancel cropping?",
      text: "All unsaved crops will be lost.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel",
      cancelButtonText: "Continue cropping",
    }).then((result) => {
      if (result.isConfirmed) {
        croppingQueue.forEach((img) => URL.revokeObjectURL(img.url));
        setCropModalMultiOpen(false);
        setCroppingQueue([]);
        setCurrentCropIndex(0);
      }
    });
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => {
      const newImages = prev.filter((_, i) => i !== index);
      if (newImages.length === 0) {
        setImageErrors(["At least 1 image required"]);
      } else {
        setImageErrors([]);
      }
      setTouched((prevTouched) => ({ ...prevTouched, images: true }));
      return newImages;
    });
  };

  const handleRemoveVideo = (index) => {
    setVideos((prev) => prev.filter((_, i) => i !== index));
    setVideoErrors([]);
  };

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleCropImage = useCallback(
    async () => {
      try {
        setLoading(true);
        const croppedImageFile = await getCroppedImg(
          imageSrc,
          croppedAreaPixels,
          "cropped_product_image.jpeg"
        );

        let fileToUse = croppedImageFile;
        if (croppedImageFile.size / 1024 / 1024 > 5) {
          const options = {
            maxSizeMB: 5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          };
          try {
            fileToUse = await imageCompression(croppedImageFile, options);
          } catch (compressionError) {
            console.error("Image compression failed:", compressionError);
          }
        }

        const errors = validateFiles([fileToUse], "image");
        if (errors.length > 0) {
          setImageErrors(errors);
          setSnackbar({
            open: true,
            severity: "error",
            message: errors.join(", "),
          });
          setLoading(false);
          return;
        }

        if (selectedProduct?.product_id) {
          const formData = new FormData();
          formData.append("media", fileToUse);
          formData.append("type", "image");
          formData.append("product_id", selectedProduct.product_id);
          formData.append("mobile_number", mobile_number);

          const response = await api.post("/product/upload_media", formData);
          if (response.status === 200) {
            const data = response.data;
            setSelectedProduct((prev) => ({
              ...prev,
              product_images: [...(prev.product_images || []), data.fileUrl],
            }));
            setSnackbar({
              open: true,
              severity: "success",
              message: "Image cropped and uploaded successfully!",
            });
            fetchProducts();
            setOpenMediaModel(false);
          }
        } else {
          setImages((prevImages) => [...prevImages, fileToUse]);
          setSnackbar({
            open: true,
            severity: "success",
            message: "Image cropped and added to form!",
          });
        }

        setOpenCropModal(false);
        setImageSrc(null);
        setImageErrors([]);
      } catch (e) {
        console.error(e);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to crop image.",
        });
      } finally {
        setLoading(false);
      }
    },
    [imageSrc, croppedAreaPixels, selectedProduct, mobile_number]
  );

  const handleVideoChange = (event) => {
    const files = Array.from(event.target.files);
    const errors = validateFiles(files, "video");
    if (errors.length > 0) {
      setVideoErrors(errors);
      return;
    }
    setVideoErrors([]);
    setVideos((prevVideos) => [...prevVideos, ...files]);
    event.target.value = "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const isValid = validateAddForm();

    if (!isValid) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please fix all validation errors before submitting",
      });
      setLoading(false);
      return;
    }

    setLoading(true);

    const formData = new FormData();

    images.forEach((file) => formData.append("images", file));
    videos.forEach((file) => formData.append("videos", file));
    formData.append(
      "specification",
      JSON.stringify(Input.specification || [])
    );

    Object.keys(Input).forEach((key) => {
      if (
        key !== "product_images" &&
        key !== "specification" &&
        key !== "product_videos"
      ) {
        let value = Input[key];
        if (key === "sku" && !value) value = "dummy";
        if (key === "discount_price") {
          value = Input.discounted_price || "0";
        }
        if (key === "gst_percentage" && Input.gst_type === "No GST") value = "";
        formData.append(key, value);
      }
    });

    formData.set("user_id", email);
    formData.append("mobile_number", mobile_number);

    console.log('Form Data:', Object.fromEntries(formData));

    try {
      const response = await api.post("/product/add", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.status) {
        handleClose();
        fetchProducts();
        Swal.fire({
          title: "Successfully Added Product!",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        handleClose();
        Swal.fire({
          title: "Error",
          text: response.data.message,
          icon: "error",
        });
      }
    } catch (error) {
      handleClose();
      Swal.fire({
        title: "Error",
        text: "An unexpected error occurred",
        icon: "error",
      });
      console.error("Error during product add:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    let cleanedValue;
    let updatedInput = { ...editInput };

    if (name === "keywords") {
      cleanedValue = Array.isArray(value)
        ? value
        : value
            .split(",")
            .map((k) => k.trim())
            .filter(Boolean);

      console.log('Keywords changed to:', cleanedValue);
      setEditKeywordsError(
        cleanedValue.length > 0 ? "" : "Please enter at least one keyword."
      );
    } else if (name === "gst_type") {
      cleanedValue = value;
      if (value === "No GST") {
        updatedInput = {
          ...updatedInput,
          gst_type: cleanedValue,
          gst_percentage: "",
        };
        setErrors((prev) => ({ ...prev, gst_percentage: "" }));
      } else {
        updatedInput = { ...updatedInput, gst_type: cleanedValue };
      }
    } else {
      cleanedValue = value;
    }

    updatedInput = { ...updatedInput, [name]: cleanedValue };

    if (!Array.isArray(updatedInput.keywords)) {
      updatedInput.keywords = (updatedInput.keywords || "")
        .toString()
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean);
    }

    const formatDiscount = (num) =>
      Number.isInteger(num)
        ? num.toString()
        : parseFloat(num.toFixed(2)).toString();

    if (name === "discount" && updatedInput.price) {
      const priceNum = parseFloat(updatedInput.price);
      const discountNum = parseFloat(cleanedValue);
      if (priceNum > 0 && discountNum >= 0) {
        const discountedPrice = priceNum * (1 - discountNum / 100);
        updatedInput.discounted_price = formatDiscount(discountedPrice);
      } else {
        updatedInput.discounted_price = "";
      }
    }

    if (name === "price") {
      if (updatedInput.discount) {
        const priceNum = parseFloat(cleanedValue);
        const discountNum = parseFloat(updatedInput.discount);
        if (priceNum > 0 && discountNum >= 0) {
          const discountedPrice = priceNum * (1 - discountNum / 100);
          updatedInput.discounted_price = formatDiscount(discountedPrice);
        } else {
          updatedInput.discounted_price = "";
        }
      }
    }

    if (name === "stock_quantity") {
      const qty = Number(cleanedValue);
      setIsDealerPriceEnabled(qty > 25);

      if (qty <= 25) {
        updatedInput.discount_price = "";
      }
    }

    console.log('Updated input:', {
      name,
      value: cleanedValue,
      discount_price: updatedInput.discount_price,
      updatedInput
    });

    setEditInput(updatedInput);

    if (editTouched[name]) {
      const validationErrors = validateProduct(updatedInput);
      setErrors(validationErrors);
    }

    setEditTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleSpecificationChange = (index, field, value) => {
    setEditInput((prevState) => {
      const newSpecs = [
        ...(prevState.specification || [{ title: "", desc: "" }]),
      ];
      newSpecs[index][field] = value;

      const errors = validateSpecifications(newSpecs);
      setSpecificationErrors(errors);

      return {
        ...prevState,
        specification: newSpecs,
        _timestamp: Date.now()
      };
    });

    setEditTouched((prev) => ({
      ...prev,
      [`specification_${index}_${field}`]: true,
      specifications: true
    }));
  };

  const addSpecificationRow = () => {
    setEditInput({
      ...editInput,
      specification: [...editInput.specification, { title: "", desc: "" }],
    });
  };

  const removeSpecificationRow = (index) => {
    const newSpecs = editInput.specification.filter((_, i) => i !== index);
    setEditInput({ ...editInput, specification: newSpecs });
  };

  const updateProduct = async (event) => {
    event.preventDefault();
    setLoading(true);

    const formData = new FormData();

    let keywordsToSend = editInput.keywords;
    if (typeof keywordsToSend === 'string') {
      keywordsToSend = keywordsToSend.split(",").map(k => k.trim()).filter(Boolean);
    }

    if (!Array.isArray(keywordsToSend) || keywordsToSend.length === 0) {
      setEditKeywordsError("Please enter at least one keyword.");
      setLoading(false);
      return;
    }

    formData.append("keywords", keywordsToSend.join(","));

    const formattedEditInput = { ...editInput };
    if (typeof editInput.specification === "object") {
      formattedEditInput.specification = JSON.stringify(
        editInput.specification
      );
    }

    Object.keys(formattedEditInput).forEach((key) => {
      if (key !== "product_images" && key !== "restock_date" && key !== "product_videos" && key !== "_timestamp") {
        let value = formattedEditInput[key];
        if (key === "gst_percentage" && formattedEditInput.gst_type === "No GST") {
          value = "";
        }
        if (key === "discount_price" && (value === null || value === "")) {
          value = "0";
        }
        formData.append(key, value);
      }
    });

    formData.append("restock_date", editInput.restock_date || "");

    try {
      const response = await api.put(
        `/product/edit/${editInput.product_id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status) {
        handleClose();
        fetchProducts();
        Swal.fire({
          title: "Successfully Updated Product!",
          icon: "success",
          timer: 1500,
        });
      } else {
        handleClose();
        Swal.fire({
          title: "Error",
          text: response.data.message,
          icon: "error",
        });
      }
    } catch (error) {
      handleClose();
      console.error("Error updating product:", error);
      Swal.fire({
        title: "Error",
        text: "An unexpected error occurred",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await api.delete(`/product/delete/${deleteProductId}`);
      if (response.data.status === 1) {
        handleClose();
        fetchProducts();
        Swal.fire({ title: "Successfully Deleted Product!", icon: "success", timer: 1500 });
      } else {
        Swal.fire({ title: "Error", text: response.data.message, icon: "error" });
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      Swal.fire({ title: "Error", text: "An unexpected error occurred", icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setInput({
      product_name: "",
      sku: "",
      price: "",
      stock_quantity: "",
      discount_price: "",
      discount: "",
      gst_type: "",
      gst_percentage: "",
      parent_category_name: "",
      specification: [{ title: "", desc: "" }],
      description: "",
      warranty_description: "",
      product_images: [],
      product_videos: [],
      keywords: [],
    });
    setEditInput({});
    setOpenContentModel(false);
    setOpenEditModal(false);
    setOpenDeleteModal(false);
    setOpenAddModal(false);
    setOpenMediaModel(false);
    setImages([]);
    setVideos([]);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setErrors({});
    setImageErrors([]);
    setVideoErrors([]);
    setKeywordsError("");
    setEditKeywordsError("");
    setSpecificationErrors([]);
    setTouched({});
    setEditTouched({});
    setCanSubmitEdit(false);

    croppingQueue.forEach((img) => URL.revokeObjectURL(img.url));
    setCroppingQueue([]);
    setCurrentCropIndex(0);
    setCropModalMultiOpen(false);
  };

  const setImagesVideos = (products) => {
    if (!products) return;
    const productsArray = Array.isArray(products) ? products : [products];
    const updatedProducts = productsArray
      .map((product) => {
        if (!product || typeof product !== "object") return null;
        return {
          ...product,
          product_images: Array.isArray(product.product_images)
            ? product.product_images.map((img) => `${img}`)
            : [],
          product_videos: Array.isArray(product.product_videos)
            ? product.product_videos.map((vid) => `${vid}`)
            : [],
        };
      })
      .filter(Boolean);

    if (updatedProducts.length > 0) {
      setSelectedProduct(
        updatedProducts.length === 1 ? updatedProducts[0] : updatedProducts
      );
      setOpenMediaModel(true);
    }
  };

  const handleAddMedia = (type) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = type === "image" ? "image/*" : "video/*";
    input.multiple = true;

    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      if (type === "image") {
        const file = files[0];
        const imageUrl = URL.createObjectURL(file);
        setImageSrc(imageUrl);
        setOpenCropModal(true);
      } else {
        setLoading(true);
        try {
          const newVideoFiles = [];
          for (const file of files) {
            const errors = validateFiles([file], "video");
            if (errors.length > 0) {
              setSnackbar({
                open: true,
                severity: "error",
                message: `Video validation failed for ${file.name}: ${errors.join(", ")}`,
              });
              continue;
            }

            if (selectedProduct?.product_id) {
              const formData = new FormData();
              formData.append("media", file);
              formData.append("type", type);
              formData.append("product_id", selectedProduct.product_id);
              formData.append("mobile_number", mobile_number);

              const response = await api.post(
                "/product/upload_media",
                formData
              );
              if (response.status === 200) {
                const data = response.data;
                setSelectedProduct((prev) => ({
                  ...prev,
                  product_videos: [
                    ...(prev.product_videos || []),
                    data.fileUrl,
                  ],
                }));
                setSnackbar({
                  open: true,
                  severity: "success",
                  message: `Video "${file.name}" uploaded successfully!`,
                });
                fetchProducts();
                setOpenMediaModel(false);
              }
            } else {
              newVideoFiles.push(file);
              setSnackbar({
                open: true,
                severity: "success",
                message: `Video "${file.name}" added to form.`,
              });
            }
          }
          if (newVideoFiles.length > 0) {
            setVideos((prevVideos) => [...prevVideos, ...newVideoFiles]);
          }
        } catch (error) {
          console.error("Error uploading video:", error);
          setSnackbar({
            open: true,
            message: `Error uploading video: ${error.message}`,
            severity: "error",
          });
        } finally {
          setLoading(false);
        }
      }
    };
    input.click();
  };

  const handleDeleteMedia = async (index, type) => {
    setLoading(true);
    const mediaKey = type === "image" ? "product_images" : "product_videos";
    const deletedKey = type === "image" ? "deleted_images" : "deleted_videos";
    let fileUrl = selectedProduct[mediaKey][index];
    const productId = selectedProduct?.product_id;

    if (!fileUrl || !productId) {
      setLoading(false);
      return;
    }

    let filePath = fileUrl.replace(baseURL + "/product/media/", "");

    try {
      const response = await api.post("/product/delete_media", {
        filePath,
        product_id: productId,
        mobile_number: mobile_number,
      });

      if (response.status === 200) {
        setSelectedProduct((prev) => {
          const updatedList = prev[mediaKey].filter((_, i) => i !== index);
          return {
            ...prev,
            [mediaKey]: updatedList,
            [deletedKey]: [...(prev[deletedKey] || []), filePath],
          };
        });
        setSnackbar({
          open: true,
          message: "Media deleted successfully!",
          severity: "success",
        });
        fetchProducts();
      }
    } catch (error) {
      console.error("Error deleting media:", error);
      setSnackbar({
        open: true,
        message: "Error deleting media",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseCropModal = () => {
    setOpenCropModal(false);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleCloseFullScreen = () => {
    setOpenFullScreen(false);
    setSelectedMedia(null);
    setIsVideo(false);
  };

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton onLogout={() => navigate("/login")} />
      </MDBox>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <ProductTable
              products={products}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e, newRowsPerPage) => {
                setRowsPerPage(newRowsPerPage);
                setPage(0);
              }}
              onView={(product) => {
                setOpenContentModel(true);
                setSelectedProduct(product);
              }}
              onMedia={(product) => setImagesVideos(product)}
              onEdit={(product) => {
                setOpenEditModal(true);

                const productToEdit = {
                  ...product,
                  keywords: Array.isArray(product.keywords)
                    ? product.keywords
                    : (product.keywords || "").split(",").map(k => k.trim()).filter(Boolean),
                  specification: Array.isArray(product.specification)
                    ? product.specification
                    : [],
                  discount_price: product.discount_price !== undefined && product.discount_price !== null
                    ? product.discount_price.toString()
                    : "",
                };

                console.log('Setting edit input with discount_price:', productToEdit.discount_price);
                setEditInput(productToEdit);
              }}
              onDelete={(id) => {
                setDeleteProductId(id);
                setOpenDeleteModal(true);
              }}
              onAddProduct={() => setOpenAddModal(true)}
            />
          </Grid>
        </Grid>
      </MDBox>

      <AddProductModal
        open={openAddModal}
        onClose={handleClose}
        input={Input}
        onChange={handleAddChange}
        onSubmit={handleSubmit}
        categories={categories}
        errors={errors}
        specificationErrors={specificationErrors}
        keywordsError={keywordsError}
        isDealerPriceEnabled={isDealerPriceEnabled}
        loading={loading}
        onSpecificationChange={handlespecificationChange}
        addSpecificationRow={addspecificationRow}
        removeSpecificationRow={removespecificationRow}
        images={images}
        videos={videos}
        imageErrors={imageErrors}
        videoErrors={videoErrors}
        onImageSelect={handleImageSelectForCrop}
        onVideoSelect={handleVideoChange}
        onRemoveImage={handleRemoveImage}
        onRemoveVideo={handleRemoveVideo}
        touched={touched}
        validateForm={validateAddForm}
        setImages={setImages}
        setImageErrors={setImageErrors}
      />

      <EditProductModal
        open={openEditModal}
        onClose={handleClose}
        input={editInput}
        onChange={handleEditChange}
        onSubmit={updateProduct}
        categories={categories}
        errors={errors}
        specificationErrors={specificationErrors}
        keywordsError={editKeywordsError}
        isDealerPriceEnabled={isDealerPriceEnabled}
        canSubmit={canSubmitEdit}
        loading={loading}
        onSpecificationChange={handleSpecificationChange}
        addSpecificationRow={addSpecificationRow}
        removeSpecificationRow={removeSpecificationRow}
        touched={editTouched}
      />

      <MediaModal
        open={openMediaModel}
        onClose={handleClose}
        tabIndex={tabIndex}
        onTabChange={(e, newIndex) => setTabIndex(newIndex)}
        product={selectedProduct}
        onAddMedia={handleAddMedia}
        onDeleteMedia={handleDeleteMedia}
        onZoomMedia={(media, type) => {
          setSelectedMedia(media);
          setIsVideo(type === "video");
          setOpenFullScreen(true);
        }}
        onRefresh={fetchProducts}
      />

      <CropModal
        open={openCropModal}
        onClose={handleCloseCropModal}
        imageSrc={imageSrc}
        crop={crop}
        zoom={zoom}
        onCropChange={setCrop}
        onZoomChange={setZoom}
        onCropComplete={onCropComplete}
        onSave={handleCropImage}
        loading={loading}
        aspectRatio={CROP_ASPECT_RATIO}
      />

      {cropModalMultiOpen && croppingQueue.length > 0 && (
        <CropModal
          open={cropModalMultiOpen}
          onClose={handleCropModalMultiClose}
          croppingQueue={croppingQueue}
          currentIndex={currentCropIndex}
          onNavigate={handleCropNavigation}
          onSaveCurrentCrop={handleSaveCurrentCrop}
          onFinish={handleFinishCropping}
          loading={loading}
          aspectRatio={CROP_ASPECT_RATIO}
        />
      )}

      <DeleteProductModal
        open={openDeleteModal}
        onClose={handleClose}
        onDelete={deleteProduct}
        loading={loading}
      />

      <ProductDetailsModal
        open={openContentModel}
        onClose={handleClose}
        product={selectedProduct}
      />

      <FullScreenMediaModal
        open={openFullScreen}
        onClose={handleCloseFullScreen}
        media={selectedMedia}
        isVideo={isVideo}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default ProductDetails;
